import { React, Component } from "react";
import PropTypes from "prop-types";
import {
    deleteApi,
    fetchApi,
    putApi,
    saveApi,
} from "../../utils/DatabaseHelper";
import Select from "react-select";
import ReactForm from "@rjsf/core";
import validator from "@rjsf/validator-ajv8";
import mailingTriggers from "./trigger/index";
import { getPlaceholderHelpHtml } from "./trigger/placeholderHelp";
import cronstrue from "cronstrue/i18n";
import { toast } from "react-toastify";
import logger from "../../logging/logger";
import { dedupeMetaFields } from "../../utils/metaFieldsHelper";
import { cloneDeep } from "lodash";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";

const triggerSelectionOptions = Object.keys(mailingTriggers).map(
    (triggerKey) => ({
        value: triggerKey,
        label: mailingTriggers[triggerKey].label,
    }),
);

class MailForm extends Component {
    constructor(props) {
        super(props);

        this.state = {
            email: {
                id: 0,
                trigger: "",
                bezeichnung: "Neu*",
                content: {},
                data: [],
            },
            validationErrors: {},
            prevPropsEmail: props.email,
            helpModalShow: false,
            metaFields: [],
        };
        this.formRefs = {}; // Referenzen für Formularelemente

        this.handleSubmit = this.handleSubmit.bind(this);
        this.onFormChange = this.onFormChange.bind(this);
        this.onInputChangeText = this.onInputChangeText.bind(this);
        this.deleteTemplate = this.deleteTemplate.bind(this);
        this.handleTriggerSelectChange =
            this.handleTriggerSelectChange.bind(this);
        this.handleFirmenSelectChange = this.handleFirmenSelectChange.bind(this);
        this.toggleHelpModal = this.toggleHelpModal.bind(this);
    }

    componentDidMount() {
        this.checkAndFilterOnLoad();
        this.loadMetaKeys();
    }

    // Verfügbare {meta_<key>}-Platzhalter: nur canMail=1 (nur diese lösen in Mails auf);
    // Superadmin alle, sonst global + eigene Firma (Backend-Scoping). Shadowing-Dedupe:
    // die Liste enthält globale UND firmenspezifische Felder mit gleichem Key.
    async loadMetaKeys() {
        try {
            const data = await fetchApi("metaSettings");
            const fields = (data.data || []).filter((f) => f.canMail === true);
            this.setState({
                metaFields: dedupeMetaFields(fields),
            });
        } catch (err) {
            logger.log("Meta-Keys konnten nicht geladen werden", err);
        }
    }

    componentDidUpdate(prevProps) {
        // When a new email is selected from the list.
        if (this.props.email.id !== prevProps.email.id) {
            this.filteredUntersuchungen = undefined;
            this.checkAndFilterOnLoad();
        }
    }

    async checkAndFilterOnLoad() {
        const { content } = this.state.email;
        const currentFilter = content?.to?.Begrenzung?.Filter;
        const currentTaetigkeit = content?.to?.Begrenzung?.taetigkeit;

        if (
            currentFilter === "Nach Untersuchungen aus dem Tätigkeitsbereich" &&
            currentTaetigkeit &&
            currentTaetigkeit.length > 0
        ) {
            try {
                const filteredUntersuchungen =
                    await this.props.filterUntersuchungen(currentTaetigkeit);
                this.filteredUntersuchungen = filteredUntersuchungen;
                this.forceUpdate();
            } catch (error) {
                logger.error(
                    "Fehler beim initialen Filtern der Untersuchungen:",
                    error,
                );
            }
        }
    }

    static buildFormSchema(triggerKey, props) {
        if (!triggerKey || !mailingTriggers[triggerKey]) {
            return null;
        }
        const formSchema = cloneDeep(mailingTriggers[triggerKey].form_schema);
        const { definitions } = formSchema;

        if (definitions?.taetigkeit) {
            definitions.taetigkeit.items.anyOf = props.taetigkeiten || [];
        }
        if (definitions?.untersuchungen) {
            let untersuchungen = props.untersuchungen || [];
            if (triggerKey === "angebotExamination") {
                untersuchungen = untersuchungen.filter(
                    (u) => u.vorsorgeart === "Angebot",
                );
            }
            definitions.untersuchungen.items.anyOf = untersuchungen;
        }
        return formSchema;
    }

    static getDerivedStateFromProps(props, state) {
        // Only update when we have new email data (different ID) or when state is not initialized
        const isNewEmail =
            !state.email ||
            !state.email.id ||
            props.email.id !== state.email.id ||
            (state.email.id <= 0 && props.email.id > 0);

        // Also update if we're dealing with a new template that just got an ID
        const shouldUpdate =
            isNewEmail ||
            state.prevPropsEmail === undefined ||
            (state.prevPropsEmail &&
                props.email.id !== state.prevPropsEmail.id);

        if (shouldUpdate) {
            const newEmail = { ...props.email };
            try {
                let content = newEmail.content;
                if (typeof content === "string" && content.trim()) {
                    content = JSON.parse(content);
                }
                if (
                    typeof content !== "object" ||
                    content === null ||
                    Array.isArray(content)
                ) {
                    content = {};
                }

                if (content.to && !content.to.type) {
                    if (content.to.email) {
                        content.to.type = "individuell";
                    } else if (
                        content.to.mitarbeiter ||
                        content.to.vorgesetzte
                    ) {
                        content.to.type = content.to.vorgesetzte
                            ? "vorgesetzte"
                            : "mitarbeiter";
                    } else {
                        content.to.type = "mitarbeiter";
                    }
                }

                if (
                    content.when &&
                    content.when.sofort !== undefined &&
                    !content.when.sofort
                ) {
                    content.when.sofort = "direkt";
                }

                newEmail.content = content;

                let data = newEmail.data;
                if (typeof data === "string" && data.trim()) {
                    data = JSON.parse(data);
                }
                if (!Array.isArray(data)) {
                    data = [];
                }
                newEmail.data = data;
            } catch (error) {
                logger.error("Failed to parse email content/data:", error);
                newEmail.content = {};
                newEmail.data = [];
            }

            const isNew =
                !newEmail.id ||
                typeof newEmail.id === "string" ||
                newEmail.id <= 0;
            if (isNew) {
                newEmail.bezeichnung = newEmail.bezeichnung || "Neu*";
            }

            return {
                email: newEmail,
                prevPropsEmail: props.email,
                validationErrors: {},
            };
        }
        return null;
    }

    async handleSubmit() {
        const { email } = this.state;
        const { content, bezeichnung } = email;
        const validationErrors = {};

        logger.log("Validate: ", { email, content, bezeichnung });

        if (!bezeichnung || bezeichnung.trim() === "") {
            validationErrors.bezeichnung = "Bezeichnung ist erforderlich";
        }
        if (!content.subject || content.subject.trim() === "") {
            validationErrors.subject = "Betreff ist erforderlich";
        }
        if (!content.text || content.text.trim() === "") {
            validationErrors.text = "Text ist erforderlich";
        }

        if (Object.keys(validationErrors).length > 0) {
            logger.log("Validierungsfehler:", validationErrors);
            this.setState({ validationErrors });
            toast.error("Bitte füllen Sie alle Pflichtfelder aus.");
            return;
        }

        let dataToSave = {
            ...email,
            content: JSON.stringify(email.content),
            data: JSON.stringify(email.data),
        };

        const isNew =
            !email.id || typeof email.id === "string" || email.id <= 0;

        // Bei neuen Objekten die ID nicht mitsenden
        if (isNew) {
            const { id, ...newDataToSave } = dataToSave;
            dataToSave = newDataToSave;
        }

        const apiCall = isNew
            ? saveApi("emails", dataToSave)
            : putApi("emails", email.id, dataToSave);

        try {
            const resp = await apiCall;
            toast.success(resp.message);
            global.emitter.emit("REFRESH_EMAIL");
        } catch (err) {
            logger.error("Save/Update failed", err);
            toast.error(err.message || "Ein Fehler ist aufgetreten.");
        }
    }

    // Hilfsfunktion zur Bereinigung der formData
    cleanOneOfData(formData, schema) {
        if (!formData || !schema || !schema.properties) {
            return formData;
        }

        const cleanedData = cloneDeep(formData);

        // Funktion zum Bereinigen von oneOf-Feldern
        const cleanOneOfFields = (data, props) => {
            if (!data || !props) return;

            Object.keys(props).forEach((key) => {
                const prop = props[key];

                // Prüfe ob es sich um ein oneOf-Feld handelt
                if (prop.oneOf && data[key] && typeof data[key] === "object") {
                    // Finde die erste gültige oneOf-Option basierend auf dem type-Feld
                    const validOption = prop.oneOf.find((option) => {
                        if (
                            data[key].type &&
                            option.properties &&
                            option.properties.type
                        ) {
                            // Wenn ein type-Feld vorhanden ist, vergleiche mit dem enum-Wert
                            return (
                                option.properties.type.enum &&
                                option.properties.type.enum.includes(
                                    data[key].type,
                                )
                            );
                        }
                        // Fallback zur alten Methode
                        if (!option.required) return true;
                        // Prüfe ob alle required-Felder vorhanden sind
                        return option.required.every(
                            (reqField) =>
                                Object.prototype.hasOwnProperty.call(
                                    data[key],
                                    reqField,
                                ) ||
                                (option.properties &&
                                    option.properties[reqField] &&
                                    option.properties[reqField].readOnly),
                        );
                    });

                    // Wenn eine gültige Option gefunden wurde, entferne nicht benötigte Felder
                    if (validOption && validOption.properties) {
                        const validFields = Object.keys(validOption.properties);
                        Object.keys(data[key]).forEach((field) => {
                            if (!validFields.includes(field)) {
                                delete data[key][field];
                            }
                        });
                    }
                }

                // Rekursive Prüfung für verschachtelte Objekte
                if (
                    prop.properties &&
                    data[key] &&
                    typeof data[key] === "object" &&
                    !Array.isArray(data[key])
                ) {
                    cleanOneOfFields(data[key], prop.properties);
                }
            });
        };

        // Bereinige die Daten
        cleanOneOfFields(cleanedData, schema.properties);

        return cleanedData;
    }

    // onFormChange can be simplified as the key-prop handles the hard reset.
    // We only need to update the content in the state.
    onFormChange = async (event) => {
        const { formData } = event;
        if (!formData) return;

        // Prüfe ob sich der Tätigkeitsbereich geändert hat und der Filter "Untersuchungen aus dem Tätigkeitsbereich" ist
        const prevFormData = this.state.email.content;
        const currentFilter = formData.to?.Begrenzung?.Filter;
        const currentTaetigkeit = formData.to?.Begrenzung?.taetigkeit;
        const prevTaetigkeit = prevFormData.to?.Begrenzung?.taetigkeit;

        // Wenn der Filter "Untersuchungen aus dem Tätigkeitsbereich" ist und sich der Tätigkeitsbereich geändert hat
        if (currentFilter === "Nach Untersuchungen aus dem Tätigkeitsbereich") {
            if (
                currentTaetigkeit &&
                JSON.stringify(currentTaetigkeit) !==
                    JSON.stringify(prevTaetigkeit)
            ) {
                try {
                    const filteredUntersuchungen =
                        await this.props.filterUntersuchungen(
                            currentTaetigkeit,
                        );

                    // Speichere die gefilterten Untersuchungen, um sie im buildFormSchema zu verwenden
                    this.filteredUntersuchungen = filteredUntersuchungen;

                    // Erzwinge ein Neurendern des Formulars mit den neuen Untersuchungen
                    this.forceUpdate();
                } catch (error) {
                    logger.error(
                        "Fehler beim Filtern der Untersuchungen:",
                        error,
                    );
                }
            }
        } else if (this.filteredUntersuchungen !== undefined) {
            // Wenn der Filter geändert wird, die gefilterten Untersuchungen zurücksetzen
            this.filteredUntersuchungen = undefined;
            this.forceUpdate();
        }

        // Hole das aktuelle Schema
        const formSchema = MailForm.buildFormSchema(this.state.email.trigger, {
            ...this.props,
            untersuchungen:
                this.filteredUntersuchungen !== undefined
                    ? this.filteredUntersuchungen
                    : this.props.untersuchungen,
        });

        // Bereinige die Formulardaten
        const cleanedData = this.cleanOneOfData(formData, formSchema);

        this.setState((prevState) => ({
            email: {
                ...prevState.email,
                content: cleanedData,
            },
        }));
    };

    onInputChangeText(event) {
        const { name, value } = event.target;
        this.setState((prevState) => ({
            email: { ...prevState.email, [name]: value },
        }));
    }

    deleteTemplate() {
        deleteApi("emails", this.state.email.id)
            .then((resp) => toast.success(resp.message))
            .catch((err) => toast.error(err.message))
            .finally(() => global.emitter.emit("REFRESH_EMAIL"));
    }

    handleTriggerSelectChange(selectedOption) {
        const newTrigger = selectedOption.value;
        this.filteredUntersuchungen = undefined;
        this.setState((prevState) => ({
            email: {
                ...prevState.email,
                trigger: newTrigger,
                content: {}, // Reset content, the key prop will handle the rest
            },
            validationErrors: {},
        }));
    }

    handleFirmenSelectChange(option) {
        // Bindung setzen/loesen: null = Global (instanzweit). Persistiert wird
        // beim Speichern des Templates (firmenId ist Teil des email-Objekts).
        this.setState((prevState) => ({
            email: { ...prevState.email, firmenId: option ? option.value : null },
        }));
    }

    getTrigger() {
        const { trigger } = this.state.email;
        return trigger && mailingTriggers[trigger]
            ? mailingTriggers[trigger]
            : null;
    }

    getUiSchema() {
        const trigger = this.getTrigger();
        if (!trigger) return {};

        const uiSchema = cloneDeep(trigger.uiSchema);

        if (this.state.email.content?.cron) {
            try {
                const cronText = cronstrue.toString(
                    this.state.email.content.cron,
                    { locale: "de" },
                );
                if (!uiSchema.cron) uiSchema.cron = {};
                uiSchema.cron["ui:help"] = cronText;
            } catch (err) {
                if (!uiSchema.cron) uiSchema.cron = {};
                uiSchema.cron["ui:help"] = err.toString();
            }
        }
        return uiSchema;
    }

    getMailContext(trigger) {
        if (!trigger || !trigger.mail_context) return [];
        if (typeof trigger.mail_context === "function") {
            return trigger.mail_context(this.state.email.content);
        }
        return trigger.mail_context;
    }

    getContextFunctions(trigger) {
        if (!trigger || !trigger.context_functions) return [];
        if (typeof trigger.context_functions === "function") {
            return trigger.context_functions(this.state.email.content);
        }
        return trigger.context_functions;
    }

    toggleHelpModal(e) {
        if (e) e.preventDefault();
        logger.log(
            "Toggle Help Modal. Current state:",
            this.state.helpModalShow,
        );
        this.setState((prevState) => ({
            helpModalShow: !prevState.helpModalShow,
        }));
    }

    renderPlaceholderHelpModal() {
        const { email } = this.state;
        if (!email.trigger) return null;

        const helpHtml = getPlaceholderHelpHtml(
            email.trigger,
            this.state.metaFields,
        );
        const triggerLabel =
            mailingTriggers[email.trigger]?.label || email.trigger;

        return (
            <Modal
                isOpen={this.state.helpModalShow}
                toggle={this.toggleHelpModal}
                size="lg"
                centered={true}
                className="placeholder-help-modal"
            >
                <ModalHeader toggle={this.toggleHelpModal}>
                    Platzhalter-Hilfe: {triggerLabel}
                </ModalHeader>
                <ModalBody>
                    <div className="placeholder-help-content">
                        <div dangerouslySetInnerHTML={{ __html: helpHtml }} />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={this.toggleHelpModal}>
                        Schließen
                    </Button>
                </ModalFooter>
            </Modal>
        );
    }

    renderContext(items, title) {
        if (!items || items.length === 0) return null;
        return (
            <>
                <span className="desc">{title}:</span>
                <br />
                {items.map((item, idx) => (
                    <span key={idx} className="desc">
                        {item},{" "}
                    </span>
                ))}
                <hr />
            </>
        );
    }

    renderMailHelperContext(items) {
        if (!items || items.length === 0) return null;
        return (
            <>
                <div className="mail-context-section">
                    <div className="mail-context-header">
                        <h6>
                            Verfügbare Platzhalter
                            <i
                                className="fas fa-info-circle placeholder-help-icon"
                                title="Platzhalter-Hilfe anzeigen"
                                onClick={(e) => this.toggleHelpModal(e)}
                                style={{
                                    cursor: "pointer",
                                    pointerEvents: "auto",
                                }}
                            ></i>
                        </h6>
                    </div>
                    <div className="mail-context-placeholders">
                        {items.map((item, idx) => (
                            <span className="placeholder-badge" key={idx}>
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
                <hr />
            </>
        );
    }

    renderTriggerForm() {
        const { email } = this.state;
        const trigger = this.getTrigger();

        if (!trigger) {
            return <p className="text-center">Bitte Auslöser wählen.</p>;
        }

        const formSchema = MailForm.buildFormSchema(email.trigger, {
            ...this.props,
            untersuchungen:
                this.filteredUntersuchungen !== undefined
                    ? this.filteredUntersuchungen
                    : this.props.untersuchungen,
        });
        if (!formSchema) {
            return (
                <p className="text-center text-danger">
                    Fehler: Formular-Schema konnte nicht geladen werden.
                </p>
            );
        }

        const uiSchema = this.getUiSchema();

        // Hole dynamische Platzhalter basierend auf dem aktuellen Form-Inhalt
        const mailContext = [
            ...this.getMailContext(trigger),
            ...this.state.metaFields.map((f) => `{meta_${f.key}} (${f.label})`),
        ];
        const contextFunctions = this.getContextFunctions(trigger);

        return (
            <div className="options">
                <pre className="d-none">
                    {JSON.stringify(email.content, null, 2)}
                </pre>

                <ReactForm
                    key={email.trigger} // The KEY change will force a full remount
                    id={`mail-form-${email.id}`} // Eindeutige ID für jedes Formular
                    schema={formSchema}
                    uiSchema={uiSchema}
                    formData={email.content}
                    transformErrors={trigger.transformErrors}
                    validator={validator}
                    onChange={this.onFormChange}
                    onSubmit={this.handleSubmit}
                    showErrorList={false}
                    omitExtraData
                    liveOmit
                >
                    <div className="mail-context mt-4">
                        {this.renderMailHelperContext(mailContext)}
                        {this.renderContext(contextFunctions, "Funktionen")}
                    </div>
                    <button type="submit" className="btn btn-primary mt-3">
                        Speichern
                    </button>
                </ReactForm>
            </div>
        );
    }

    render() {
        const { email } = this.state;
        const trigger = this.getTrigger();
        const triggerSelectionValue = email.trigger
            ? { value: email.trigger, label: trigger?.label }
            : null;
        const firmenSelectionValue = this.props.firmenOptions
            ? this.props.firmenOptions.find(
                  (option) => option.value === email.firmenId,
              ) ||
              // Fallback Global nur fuer wirklich ungebundene Templates - sonst
              // wuerde ein gebundenes Template waehrend des Firmen-Ladens als
              // Global angezeigt und ein Speichern wuerde die Bindung loesen
              (email.firmenId == null ? this.props.firmenOptions[0] : null)
            : null;

        return (
            <div className="card">
                <div className="card-header d-flex">
                    <h5 className="mb-0 col pl-0">
                        <button
                            className="btn btn-link pl-0"
                            type="button"
                            data-toggle="collapse"
                            data-target={`#collapse-${email.id}`}
                            aria-expanded="true"
                            aria-controls={`collapse-${email.id}`}
                        >
                            {email.bezeichnung} - Auslöser: "
                            {trigger?.label || "nicht zugewiesen"}"
                        </button>
                    </h5>
                    <button
                        type="button"
                        disabled={
                            !email.id ||
                            typeof email.id === "string" ||
                            email.id <= 0
                        }
                        className="btn btn-sm btn-default"
                        onClick={this.deleteTemplate}
                    >
                        <i className="fas fa-trash-alt"></i>
                    </button>
                </div>
                <div
                    id={`collapse-${email.id}`}
                    className="collapse"
                    aria-labelledby="heading"
                    data-parent="#accordionEmails"
                >
                    <div className="card-body">
                        <div className="form-group">
                            <label htmlFor="bezeichnung">Bezeichnung *</label>
                            <input
                                type="text"
                                id="bezeichnung"
                                name="bezeichnung"
                                className={`form-control ${this.state.validationErrors.bezeichnung ? "is-invalid" : ""}`}
                                value={email.bezeichnung}
                                onChange={this.onInputChangeText}
                            />
                            {this.state.validationErrors.bezeichnung && (
                                <div className="invalid-feedback">
                                    {this.state.validationErrors.bezeichnung}
                                </div>
                            )}
                        </div>
                        <div className="form-group">
                            <label htmlFor="trigger">Versand Auslöser:</label>
                            <Select
                                placeholder="Auswählen"
                                options={triggerSelectionOptions}
                                value={triggerSelectionValue}
                                name="trigger"
                                className="basic-multi-select"
                                onChange={this.handleTriggerSelectChange}
                                theme={(theme) => ({
                                    ...theme,
                                    colors: {
                                        ...theme.colors,
                                        primary25: "#ffeebe",
                                        primary: "#ffd370",
                                    },
                                })}
                            />
                            {trigger && (
                                <div className="trigger-description">
                                    <span className="desc">{trigger.desc}</span>
                                </div>
                            )}
                        </div>
                        {this.props.firmenOptions && (
                            <div className="form-group">
                                <label>Firmen-Bindung:</label>
                                <Select
                                    placeholder="Auswählen"
                                    options={this.props.firmenOptions}
                                    value={firmenSelectionValue}
                                    isClearable={false}
                                    className="basic-multi-select"
                                    onChange={this.handleFirmenSelectChange}
                                    theme={(theme) => ({
                                        ...theme,
                                        colors: {
                                            ...theme.colors,
                                            primary25: "#ffeebe",
                                            primary: "#ffd370",
                                        },
                                    })}
                                />
                                <div className="trigger-description">
                                    <span className="desc">
                                        {"Global: Template gilt instanzweit. Gebunden: Nur diese Firma sieht und nutzt das Template."}
                                    </span>
                                </div>
                            </div>
                        )}
                        {this.renderTriggerForm()}
                    </div>
                </div>
                {this.renderPlaceholderHelpModal()}
            </div>
        );
    }
}

MailForm.propTypes = {
    email: PropTypes.object.isRequired,
    taetigkeiten: PropTypes.array,
    untersuchungen: PropTypes.array,
    filterUntersuchungen: PropTypes.func,
    firmenOptions: PropTypes.array,
};

MailForm.defaultProps = {
    taetigkeiten: [],
    untersuchungen: [],
    filterUntersuchungen: async () => [],
    firmenOptions: undefined,
};

export default MailForm;
